import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { Seo } from "@/components/site/Seo";
import { useI18n } from "@/i18n/I18nContext";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`${t("login.welcome")} ${res.user.name || ""}`);
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] grid-bg flex items-center justify-center px-5 relative overflow-hidden">
      <Seo noindex title="Connexion espace client | BBR.GO" path="/connexion" />
      <div className="aura w-[500px] h-[500px] bg-blue-700/20 -top-32 -left-20" />
      <div className="aura w-[400px] h-[400px] bg-violet-700/20 bottom-0 right-0" />
      <div className="absolute top-5 right-5"><LanguageSelector /></div>

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-300 mb-8">
          <ArrowLeft className="w-4 h-4" /> {t("login.back")}
        </Link>

        <div className="glass-strong rounded-3xl p-8 sm:p-10" data-testid="login-card">
          <Logo size="text-3xl" />
          <h1 className="mt-6 font-display font-bold text-2xl text-white">{t("login.title")}</h1>
          <p className="mt-2 text-sm text-zinc-400">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">{t("login.email")}</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@entreprise.fr"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-zinc-600 focus-ring" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">{t("login.password")}</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-zinc-600 focus-ring" />
              </div>
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full bg-brand-gradient text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity duration-300 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? t("login.connecting") : <>{t("login.submit")} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-zinc-600">{t("login.footer")}</p>
      </div>
    </div>
  );
}
